import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import Loader from "../Layouts/Loader/Loader";
import axiosInstance, { handleApiError } from "../services/axiosService";

import { groupedMenuData } from "../Layouts/Navbar/navbarUtils";

const AppOrgContextApi = createContext();

// Helper function to determine mode from current URL
const getModeFromPath = (pathname) => {
  // 1. Split the URL into segments to find the current route
  const pathSegments = pathname.split("/").filter(Boolean);
  const routeIndex = pathSegments.indexOf("app");

  // 2. Check if we have a valid app route
  if (routeIndex !== -1 && pathSegments.length > routeIndex + 2) {
    const currentRoute = pathSegments[routeIndex + 2]; // e.g., "screen", "statemachine"

    // 3. Iterate through our configuration to find which mode owns this route
    for (const [modeKey, menuItems] of Object.entries(groupedMenuData)) {
      // 4. Check if the current route exists in this mode's menu items
      if (menuItems.some((item) => item.route === currentRoute)) {
        return modeKey; // Found it! Return "m1", "m2", etc.
      }
    }
  }

  return "m1";
};

const useAppOrgContext = () => useContext(AppOrgContextApi);
const AppOrgContext = ({ children }) => {
  const location = useLocation();
  const [appOrgNames, setAppOrgnames] = useState({
    orgName: "",
    appName: "",
    orgLogo: ""
  });
  const [application, setApplication] = useState({});
  const [applicationUrl, setApplicationUrl] = useState({});
  const [allApplications, setAllApplications] = useState([]);
  const currentChecksum = useRef();
  const { orgId, appId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [organisations, setOrganisations] = useState([]);

  const [mode, setMode] = useState(() => getModeFromPath(location.pathname));

  const fetchOrganisations = async () => {
    try {
      const { data } = await axiosInstance.get(`/organisations`);
      setOrganisations(data);
    } catch (error) {
      handleApiError(error);
    }
  };

  // const settingInitialName = async () => {
  //   try {
  //     setIsLoading(true);
  //     let obj = {
  //       orgName: "",
  //       appName: "",
  //       orgLogo: ""
  //     };
  //     if (orgId) {
  //       const { data: orgData } = await axiosInstance.get(`/organisations/${orgId}`);
  //       const { data } = await axiosInstance.get(`/organisations/${orgId}/apps`);
  //       setAllApplications(data);
  //       obj.orgName = orgData.name;
  //       obj.orgLogo = orgData.logo;
  //     }
  //     if (!window.location.pathname.includes("aiapp") && appId) {
  //       const { data: appData } = await axiosInstance.get(`/organisations/${orgId}/apps/${appId}`);
  //       setApplication(appData);
  //       const { data: infraData } = await axiosInstance.get(`/application/infra`, {
  //         timeout: 50000,
  //         headers: {
  //           "Zino-App-Id": appId
  //         }
  //       });
  //       setApplicationUrl({
  //         status: infraData.status,
  //         web_url: infraData.web_url,
  //         pwa_url: infraData.pwa_url
  //       });
  //       obj.appName = appData.name;
  //     }
  //     setAppOrgnames(obj);
  //     setIsLoading(false);
  //   } catch (error) {
  //     handleError(error);
  //   }
  // };

  const settingInitialName = async () => {
    try {
      setIsLoading(true);
      let obj = {
        orgName: "",
        appName: "",
        orgLogo: ""
      };

      const promises = [];

      if (orgId) {
        const orgPromise = axiosInstance.get(`/organisations/${orgId}`).then((response) => {
          obj.orgName = response.data.name;
          obj.orgLogo = response.data.logo;
        });
        promises.push(orgPromise);

        const appsPromise = axiosInstance.get(`/organisations/${orgId}/apps`).then((response) => {
          setAllApplications(response.data);
        });
        promises.push(appsPromise);
      }

      if (!window.location.pathname.includes("aiapp") && appId) {
        const appPromise = axiosInstance.get(`/organisations/${orgId}/apps/${appId}`).then((response) => {
          setApplication(response.data);
          obj.appName = response.data.name;
        });
        promises.push(appPromise);

        const infraPromise = axiosInstance
          .get(`/application/infra`, {
            timeout: 50000,
            headers: {
              "Zino-App-Id": appId
            }
          })
          .then((response) => {
            const { web_url, status } = response.data;
            setApplicationUrl((pre) => {
              if (pre) {
                return {
                  ...pre,
                  web_url,
                  status
                };
              } else {
                return {
                  web_url,
                  status
                };
              }
            });
          });
        const stageInfraPromise = axiosInstance
          .get(`/application/staging-infra`, {
            timeout: 50000,
            headers: {
              "Zino-App-Id": appId
            }
          })
          .then((response) => {
            const { web_url, status } = response.data;
            setApplicationUrl((pre) => {
              if (pre) {
                return {
                  ...pre,
                  staging_url: web_url,
                  staging_status: status
                };
              } else {
                return {
                  staging_url: web_url,
                  staging_status: status
                };
              }
            });
          });
        promises.push(infraPromise);
        promises.push(stageInfraPromise);
      }

      // Wait for all promises to resolve
      await Promise.all(promises);

      // Set app org names after all promises are resolved
      setAppOrgnames(obj);
    } catch (error) {
      // handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    settingInitialName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppOrgContextApi.Provider
      value={{
        appOrgNames,
        applicationUrl,
        application,
        allApplications,
        currentChecksum,
        organisations,
        setOrganisations,
        setAllApplications,
        setApplication,
        setAppOrgnames,
        setApplicationUrl,
        fetchOrganisations,
        mode,
        setMode
      }}
    >
      {isLoading ? (
        <div className="w_100_vw h_100_vh">
          <Loader />
        </div>
      ) : (
        children
      )}
    </AppOrgContextApi.Provider>
  );
};

export default AppOrgContext;
export { useAppOrgContext };
